import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Link } from 'react-router-dom';
import { 
    Volume2, 
    TrendingUp, 
    TrendingDown,
    Minus,
    ArrowRight,
    Loader2
} from 'lucide-react';

/**
 * Mini Communication Score Card - For embedding in module pages
 * Shows the user's communication score for the specific module context
 * with a link to view more details on the full profile page
 */
const MiniCommunicationScoreCard = ({ 
    moduleName = null, // Optional: 'role_play', 'inside_sales', 'sourcing_labs', 'rpat', etc.
    variant = 'default', // 'default', 'compact', 'inline'
    showLink = true,
    className = ''
}) => {
    const { api } = useAuth();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    useEffect(() => {
        fetchScore();
    }, [moduleName]);

    const fetchScore = async () => {
        try {
            // If module name is provided, get module-specific score
            const endpoint = moduleName 
                ? `/communication-score/module/${moduleName}`
                : '/communication-score/quick-stats';
            
            const response = await api.get(endpoint);
            setData(response.data);
        } catch (error) {
            console.error('Failed to fetch communication score:', error);
        } finally {
            setLoading(false);
        }
    };

    const getScoreColor = (score) => {
        if (score >= 80) return 'text-emerald-500';
        if (score >= 60) return 'text-amber-500';
        return 'text-rose-500';
    };

    const getScoreBgColor = (score) => {
        if (score >= 80) return 'bg-emerald-500/10 border-emerald-500/30';
        if (score >= 60) return 'bg-amber-500/10 border-amber-500/30';
        return 'bg-rose-500/10 border-rose-500/30';
    };

    const getTrendIcon = (direction) => {
        if (direction === 'improving') return <TrendingUp className="h-3 w-3 text-emerald-500" />;
        if (direction === 'declining') return <TrendingDown className="h-3 w-3 text-rose-500" />;
        return <Minus className="h-3 w-3 text-muted-foreground" />;
    };

    if (loading) {
        return (
            <div className={`flex items-center gap-2 ${className}`}>
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Loading score...</span>
            </div>
        );
    }

    // No data or no sessions
    if (!data || (data.has_data === false && !moduleName) || (moduleName && !data.data?.has_data)) {
        if (variant === 'inline') {
            return null; // Don't show anything for inline variant if no data
        }
        return (
            <Link 
                to="/communication-profile" 
                className={`flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-dashed border-muted-foreground/30 hover:border-primary/50 transition-colors ${className}`}
                data-testid="mini-comm-score-empty"
            >
                <Volume2 className="h-5 w-5 text-muted-foreground" />
                <div className="flex-1">
                    <p className="text-sm text-muted-foreground">
                        {moduleName ? 'Complete voice sessions to see your score' : 'No communication data yet'}
                    </p>
                </div>
                {showLink && <ArrowRight className="h-4 w-4 text-muted-foreground" />}
            </Link>
        );
    }

    // Get the score - different structure for module vs quick-stats
    const score = moduleName 
        ? (data.data?.communication_score || data.data?.avg_score || data.data?.voice_score || data.overall_score || 0)
        : (data.score || 0);
    
    const sessions = moduleName 
        ? (data.data?.sessions || 0)
        : (data.sessions || 0);

    // Compact inline badge version
    if (variant === 'inline') {
        return (
            <Link 
                to="/communication-profile"
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${getScoreBgColor(score)} border transition-colors hover:opacity-80 ${className}`}
                data-testid="mini-comm-score-inline"
            >
                <Volume2 className="h-3.5 w-3.5" />
                <span className={`text-sm font-semibold ${getScoreColor(score)}`}>
                    {Math.round(score)}%
                </span>
            </Link>
        );
    }

    // Compact card version
    if (variant === 'compact') {
        return (
            <Link 
                to="/communication-profile"
                className={`flex items-center gap-3 p-2 rounded-lg bg-muted/30 border hover:border-primary/50 transition-colors ${className}`}
                data-testid="mini-comm-score-compact"
            >
                <div className="relative">
                    <svg className="w-10 h-10 transform -rotate-90">
                        <circle
                            cx="20"
                            cy="20"
                            r="16"
                            stroke="currentColor"
                            strokeWidth="3"
                            fill="none"
                            className="text-muted/30"
                        />
                        <circle
                            cx="20"
                            cy="20"
                            r="16"
                            stroke="currentColor"
                            strokeWidth="3"
                            fill="none"
                            strokeDasharray={`${(score / 100) * 100} 100`}
                            className={getScoreColor(score)}
                            strokeLinecap="round"
                        />
                    </svg>
                    <span className={`absolute inset-0 flex items-center justify-center text-xs font-bold ${getScoreColor(score)}`}>
                        {Math.round(score)}
                    </span>
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">Communication Score</p>
                    <p className="text-xs text-muted-foreground">{sessions} sessions</p>
                </div>
                {showLink && <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />}
            </Link>
        );
    }

    // Default card version
    return (
        <div 
            className={`p-4 rounded-xl ${getScoreBgColor(score)} border ${className}`}
            data-testid="mini-comm-score-card"
        >
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Volume2 className="h-5 w-5 text-primary" />
                    <span className="font-medium text-sm">Communication Score</span>
                </div>
                {moduleName && (
                    <Badge variant="outline" className="text-xs capitalize">
                        {moduleName.replace(/_/g, ' ')}
                    </Badge>
                )}
            </div>
            
            <div className="flex items-center gap-4">
                {/* Score Circle */}
                <div className="relative shrink-0">
                    <svg className="w-16 h-16 transform -rotate-90">
                        <circle
                            cx="32"
                            cy="32"
                            r="26"
                            stroke="currentColor"
                            strokeWidth="5"
                            fill="none"
                            className="text-muted/20"
                        />
                        <circle
                            cx="32"
                            cy="32"
                            r="26"
                            stroke="currentColor"
                            strokeWidth="5"
                            fill="none"
                            strokeDasharray={`${(score / 100) * 163} 163`}
                            className={getScoreColor(score)}
                            strokeLinecap="round"
                        />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className={`text-lg font-bold ${getScoreColor(score)}`}>
                            {Math.round(score)}
                        </span>
                        <span className="text-[10px] text-muted-foreground">/ 100</span>
                    </div>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        {data.trends?.direction && getTrendIcon(data.trends.direction)}
                        <span className="text-xs text-muted-foreground capitalize">
                            {data.trends?.direction || 'stable'}
                        </span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                        Based on {sessions} voice session{sessions !== 1 ? 's' : ''}
                    </p>
                    {data.tips?.[0] && (
                        <p className="text-xs text-primary line-clamp-1">
                            Tip: {data.tips[0]}
                        </p>
                    )}
                </div>
            </div>

            {/* View Details Link */}
            {showLink && (
                <Link 
                    to="/communication-profile"
                    className="flex items-center justify-center gap-1 mt-3 pt-3 border-t border-current/10 text-xs text-primary hover:underline"
                >
                    View Detailed Analysis
                    <ArrowRight className="h-3 w-3" />
                </Link>
            )}
        </div>
    );
};

export default MiniCommunicationScoreCard;
